/* eslint-disable @typescript-eslint/no-explicit-any */
import { Course, CourseEnrollment } from '../types';
import axiosClient from './axiosClient';

// --- Course API Calls ---

export const createCourse = async (courseData: Partial<Course>): Promise<Course> => {
  const response = await axiosClient.post('/courses', courseData);
  return response.data;
};

export const updateCourse = async (courseId: string, courseData: Partial<Course>): Promise<Course> => {
  const response = await axiosClient.patch(`/courses/${courseId}`, courseData);
  return response.data;
};

export const deleteCourse = async (courseId: string): Promise<void> => {
  await axiosClient.delete(`/courses/${courseId}`);
};

export const getCourseById = async (courseId: string): Promise<Course | null> => {
  try {
    const response = await axiosClient.get(`/courses/${courseId}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching course:", error);
    throw error;
  }
};

export const getCoursesByAuthor = async (
  authorId: string,
  includeUnpublished: boolean = false
): Promise<Course[]> => {
  const response = await axiosClient.get(`/courses/author/${authorId}`, {
    params: { includeUnpublished: includeUnpublished ? 'true' : 'false' },
  });
  return response.data;
};

export interface CourseFilter {
  category?: string | string[];
  level?: string;
  priceRange?: [number, number];
  /** Only courses that cost nothing. The server matches this on `price_ngn`. */
  free?: boolean;
  featured?: boolean;
  page?: number;
  limit?: number;
  status?: 'published' | 'draft' | 'all';
  search?: string;
  fields?: string; // Added for future flexibility and documentation
}

export const getCourses = async (filter: CourseFilter = {}): Promise<{ courses: Course[]; totalPages: number }> => {
  const response = await axiosClient.get('/courses', { params: filter });
  // Backend returns { courses, totalCount }, map to totalPages
  return {
    courses: response.data.courses,
    totalPages: response.data.totalPages || Math.ceil((response.data.totalCount || 0) / (filter.limit || 12))
  };
};

export const getAllCoursesForAdmin = async (): Promise<Course[]> => {
  const { courses } = await getCourses({ status: 'all', limit: 1000 });
  return courses;
};



export const setCourseFeaturedStatus = async (courseId: string, featured: boolean): Promise<void> => {
  await axiosClient.patch(`/courses/${courseId}/featured`, { featured });
};


// --- Course Enrollment API Calls ---

export const fetchEnrolledCoursesApi = async (uid: string): Promise<CourseEnrollment[]> => {
  const response = await axiosClient.get(`/users/${uid}/enrollments`);
  /**
   * The signature promises an array; `response.data` is whatever arrived. A 304,
   * an empty body or an error shape all satisfied TypeScript here and then blew
   * up as "enrolledCourses.map is not a function" three layers away, blanking
   * the whole profile page. Honour the contract at the boundary that claims it.
   */
  return Array.isArray(response.data) ? response.data : [];
};

export const enrollInCourseApi = async (
  uid: string,
  courseId: string,
  enrollmentType: 'paid' | 'scholarship' | 'free',
  paymentReference?: string
): Promise<CourseEnrollment> => {
  const response = await axiosClient.post(`/courses/${courseId}/enroll`, { userId: uid, enrollmentType, paymentReference });
  return response.data;
};

export const updateCourseEnrollmentApi = async (
  enrollmentId: string,
  updates: Partial<CourseEnrollment>
): Promise<CourseEnrollment> => {
  const response = await axiosClient.patch(`/enrollments/${enrollmentId}`, updates);
  return response.data;
};

export const unenrollFromCourseApi = async (enrollmentId: string): Promise<void> => {
  await axiosClient.delete(`/enrollments/${enrollmentId}`);
};

export const checkIfUserHasCourseAccess = async (userId: string, courseId: string): Promise<boolean> => {
  const response = await axiosClient.get(`/users/${userId}/courses/${courseId}/access`);
  return response.data.hasAccess;
};

export const markLessonAsCompleted = async (
  enrollmentId: string,
  lessonId: string
): Promise<CourseEnrollment> => {
  const response = await axiosClient.patch(`/enrollments/${enrollmentId}`, {
    $push: { completedLessons: lessonId },
  });
  return response.data;
};

export const getEnrollmentByUserIdAndCourseId = async (userId: string, courseId: string): Promise<CourseEnrollment | null> => {
  try {
    const response = await axiosClient.get(`/users/${userId}/enrollments/${courseId}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching enrollment:", error);
    throw error;
  }
};

export const getCourseBySlug = async (slug: string): Promise<Course | null> => {
  try {
    const response = await axiosClient.get(`/courses/slug/${slug}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching course by slug:", error);
    throw error;
  }
};

/**
 * The categories that exist across published courses.
 *
 * The catalogue filter needs the full set, which is a property of the catalogue
 * rather than of whichever page of results is currently loaded.
 */
export const getCourseCategories = async (): Promise<string[]> => {
  try {
    const response = await axiosClient.get('/courses/categories');
    return response.data?.categories ?? [];
  } catch (error) {
    console.error('Error fetching course categories:', error);
    return [];
  }
};

/**
 * How many published courses the catalogue holds.
 *
 * `getCourses` throws the envelope's `totalCount` away in favour of a page
 * count, and pulling twelve full course summaries to learn one number is a lot
 * of payload — so this asks for a single row and reads the total off the
 * envelope. Public: the server counts only published courses for a caller who
 * is not staff.
 */
export const getPublishedCourseCount = async (): Promise<number> => {
  const response = await axiosClient.get('/courses', { params: { limit: 1 } });
  return Number(response.data?.totalCount) || 0;
};
